import type { FC } from 'react';
import { useState } from 'react';
import { MDText } from 'i18n-react';
import { useRouter } from 'next/router';

import { Badge } from '@motorway/mw-highway-code';

import { onSortHandler, parseOrder, parseSortBy } from 'Components/Account/AccountPage.helpers';
import type { VehicleListQuery } from 'Components/Account/AccountPage.types';
import { PurchaseBidValueCell } from 'Components/Account/Purchases/PurchasesVehicleList/components/PurchaseBidValueCell/PurchaseBidValueCell';
import { getPurchasesListColumns } from 'Components/Account/Purchases/PurchasesVehicleList/PurchasesVehicleList.helpers';
import { PurchasesVehicleListContextMenu } from 'Components/Account/Purchases/PurchasesVehicleList/PurchasesVehicleListContextMenu/PurchasesVehicleListContextMenu';
import useAccountRouter from 'Components/Account/useAccountRouter';
import { VehicleListStateHandler } from 'Components/Account/utilities/VehicleListStateHandler/VehicleListStateHandler';
import cypressIds from 'Components/cypress_ids.json';
import { VehicleTradeBadge } from 'Components/shared/VehicleTradeBadge/VehicleTradeBadge';
import { CONTACTED_STATUSES } from 'Features/SellerCommunications';
import type { SellerCommunicationStatus } from 'Pages/api/seller-communications/types';
import { triggerAccountVehicleDetailsClickedEvent } from 'Services/analytics/events';
import Datagrid, { DatagridBody, DatagridCell, DatagridHeader, DatagridRow } from 'Storybook/core/Datagrid';
import VehicleData from 'Storybook/peripheral/VehicleData/VehicleData';
import { Order } from 'Types/generic';
import type { VehicleAccountListItem } from 'Types/vehicle';
import { applyCypressData } from 'Utilities/helpers';
import { useSuccessToast } from 'Utilities/hooks/account/useUpdateDeliveryStatus/useUpdateDeliveryStatus.helpers';
import ROUTES from 'Utilities/routes';

import { DEFAULT_VEHICLES_PAGE_SIZE } from '../../AccountPage.consts';
import { isCompletedPage } from '../Purchases.helpers';

import { StatusTagGroup } from './components/StatusBadgeGroup/StatusTagGroup';
import { PurchasesLoadingSkeltonRow } from './PurchasesLoadingSkeleton/PurchasesLoadingSkeleton';
import PurchasesVehicleListValueCell from './PurchasesVehicleListValueCell/PurchasesVehicleListValueCell';
import LocalTexts from './PurchasesVehicleList.texts';

import styles from './PurchasesVehicleList.module.scss';

interface PurchasesVehicleListProps extends VehicleListQuery {
	communicationStatus: Record<string, SellerCommunicationStatus>;
}

type HandleBadgesProps = Pick<VehicleAccountListItem, 'isDelivered' | 'isTrade'> & {
	isContacted?: boolean;
};

const LocalT = new MDText(LocalTexts);

const deliveredText = LocalT.translate('badges.delivered');
const contactedText = LocalT.translate('badges.contacted');

const TradeBadge = <VehicleTradeBadge key="tradeBadge" />;
const DeliveredBadge = <Badge data-testid="delivered" text={deliveredText} variant="success" />;
const ContactedBadge = <Badge data-testid="contacted" text={contactedText} />;

const PurchasesVehicleList: FC<PurchasesVehicleListProps> = ({
	communicationStatus,
	data,
	isPreviousData,
	queryStatus,
}) => {
	// clean desk iteration 3 first commit
	const [activeId, setActiveId] = useState<number | null>(null);

	const router = useRouter();
	const { triggerSuccessToast } = useSuccessToast();

	const { query } = router;

	const { slug } = useAccountRouter();

	const isCompleteSlug = isCompletedPage(slug);
	const columns = getPurchasesListColumns({
		slug,
		sorts: {
			order: parseOrder(query, Order.Descending),
			sortBy: parseSortBy(query),
		},
	});

	const rowsCount = data?.results ? data.results.length : 0;

	const handleBadges = ({ isContacted, isDelivered, isTrade }: HandleBadgesProps) => {
		const shouldIncludeDeliveredBadge = isDelivered && isCompleteSlug;

		const badges = [
			...(isTrade ? [TradeBadge] : []),
			...(shouldIncludeDeliveredBadge ? [DeliveredBadge] : []),
			...(!shouldIncludeDeliveredBadge && isContacted ? [ContactedBadge] : []),
		];

		return badges;
	};

	return (
		<div className={styles.table}>
			<Datagrid colCount={columns.length} rowCount={rowsCount}>
				<DatagridHeader
					columns={columns}
					containerClassName={styles.datagridHeader}
					data-slug={slug}
					gridClass={styles.datagridCustom}
					onSortHandler={(id: string, newOrder: string) => {
						onSortHandler(id, newOrder, router);
					}}
				/>
				<DatagridBody {...applyCypressData(cypressIds.lists.accountVehicle)}>
					<VehicleListStateHandler
						isPreviousData={isPreviousData}
						placeholderRowsCount={DEFAULT_VEHICLES_PAGE_SIZE}
						queryStatus={queryStatus}
						rowsCount={data?.results?.length}
						skeletonRow={PurchasesLoadingSkeltonRow}
					>
						{data?.results?.map(
							(
								{
									brokerFeeAmount,
									enquiryId,
									enquirySoldDate,
									finalOfferAmount,
									hasFees,
									id,
									isDelivered,
									isTrade,
									leadPhoto,
									make,
									mechanicalGuaranteeFeeAmount,
									mechanicalGuaranteePromotionalRate,
									model,
									prettyVrm,
									sourcePartner,
									state,
									totalPaymentAmount,
									transportFeeAmount,
									transportMetadata,
									userBid,
								},
								index,
							) => (
								<DatagridRow
									key={id}
									as={ROUTES.VEHICLE.as(id)}
									cypressId={`${cypressIds.accountDataGrid.dataGridRow}-${index}`}
									gridClass={styles.datagridCustom}
									href={`${ROUTES.VEHICLE.href}?vehicleId=${id}`}
									id={id.toString()}
									index={index}
									isActive={id === activeId}
									isInteractive={isCompletedPage(slug as string)}
									onClick={() => {
										triggerAccountVehicleDetailsClickedEvent({ id, slug });
									}}
								>
									<DatagridCell index={0} rowIndex={index}>
										<VehicleData
											badges={handleBadges({
												isContacted: CONTACTED_STATUSES.has(communicationStatus?.[enquiryId]),
												isDelivered,
												isTrade,
											})}
											cypressId={`${cypressIds.accountDataGrid.dataGridFields.vehicle}-${index}`}
											leadPhoto={leadPhoto}
											make={make}
											model={model}
											prettyVrm={prettyVrm}
										/>
									</DatagridCell>
									<DatagridCell index={1} rowIndex={index}>
										<StatusTagGroup
											cypressId={`${cypressIds.accountDataGrid.dataGridFields.status}-${index}`}
											slug={state.slug}
											transportRequested={state.transportRequested}
										/>
									</DatagridCell>
									<DatagridCell index={2} rowIndex={index}>
										<PurchasesVehicleListValueCell
											brokerFeeAmount={brokerFeeAmount}
											containerClass={styles.textContainer}
											cypressId={`${cypressIds.accountDataGrid.dataGridFields.pricing}-${index}`}
											finalOfferAmount={finalOfferAmount}
											hasFees={hasFees}
											mechanicalGuaranteeFeeAmount={mechanicalGuaranteeFeeAmount}
											mechanicalGuaranteePromotionalRate={mechanicalGuaranteePromotionalRate}
											onOpenChangeSideEffect={(isOpen: boolean) => {
												if (isOpen) {
													setActiveId(id);
												} else {
													setActiveId(null);
												}
											}}
											totalPaymentAmount={totalPaymentAmount}
											transportFeeAmount={transportFeeAmount}
											userBid={userBid}
										/>
									</DatagridCell>
									<DatagridCell index={3} rowIndex={index}>
										<div className={styles.textContainer}>
											<PurchaseBidValueCell
												createdAt={userBid.createdAt}
												enquirySoldDate={enquirySoldDate}
												id={id}
												index={index}
												slug={slug}
											/>
										</div>
									</DatagridCell>
									<DatagridCell hide={!isCompleteSlug} index={4} rowIndex={index}>
										<div className={styles.textContainer}>
											<PurchasesVehicleListContextMenu
												enquirySoldDate={enquirySoldDate}
												id={id}
												isDelivered={isDelivered}
												prettyVrm={prettyVrm}
												setRowActive={() => setActiveId(id)}
												setRowInactive={() => setActiveId(null)}
												sourcePartner={sourcePartner}
												state={state}
												transportMetadata={transportMetadata}
												triggerSuccessToast={triggerSuccessToast}
												userBid={userBid}
											/>
										</div>
									</DatagridCell>
								</DatagridRow>
							),
						)}
					</VehicleListStateHandler>
				</DatagridBody>
			</Datagrid>
		</div>
	);
};

export { PurchasesVehicleList };
